import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { reelFeed, toggleTrackLike, toggleLike, toggleSubscribe, recordTrackPlay, resolveFileUrl } from '../lib/api';
import { useApp } from '../context/AppContext';
import { toast } from 'sonner';
import { Heart, Play, Pause, UserPlus, UserCheck, Share2, ChevronUp, ChevronDown, Music, Film, Loader2, Volume2, VolumeX } from 'lucide-react';

// Vertical full-screen TikTok-style reel for music tracks + music videos.
// Swipe / arrow-key / scroll to change items. Autoplay with mute by default (browser policy).

function ItemCard({ item, active, muted, onToggleMute, onLike, onFollow }) {
  const mediaRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [liked, setLiked] = useState(!!item.liked);
  const [likes, setLikes] = useState(item.likes_count || 0);
  const [following, setFollowing] = useState(!!item.following);
  const [playedPing, setPlayedPing] = useState(false);

  useEffect(() => {
    setLiked(!!item.liked); setLikes(item.likes_count || 0); setFollowing(!!item.following);
  }, [item.id]);

  useEffect(() => {
    const m = mediaRef.current;
    if (!m) return;
    if (active) {
      m.currentTime = 0;
      m.muted = muted;
      m.play().then(() => setPlaying(true)).catch(() => setPlaying(false));
      if (!playedPing && item.kind === 'track') {
        recordTrackPlay(item.id); setPlayedPing(true);
      }
    } else {
      try { m.pause(); } catch {}
      setPlaying(false);
    }
  }, [active, muted]); // eslint-disable-line

  const togglePlay = () => {
    const m = mediaRef.current; if (!m) return;
    if (m.paused) { m.play(); setPlaying(true); } else { m.pause(); setPlaying(false); }
  };

  const handleLike = async () => {
    const prev = liked; const prevCount = likes;
    setLiked(!prev); setLikes(prev ? prevCount - 1 : prevCount + 1);
    try {
      const r = await onLike(item);
      if (r && typeof r.liked === 'boolean') { setLiked(r.liked); setLikes(r.likes_count ?? likes); }
    } catch { setLiked(prev); setLikes(prevCount); toast.error('Could not like'); }
  };

  const handleFollow = async () => {
    if (!item.channel_id) { toast.info('No channel yet for this artist'); return; }
    setFollowing(f => !f);
    try { const r = await onFollow(item.channel_id); if (r && typeof r.subscribed === 'boolean') setFollowing(r.subscribed); }
    catch { setFollowing(f => !f); toast.error('Could not follow'); }
  };

  const share = async () => {
    const url = window.location.origin + (item.kind === 'track'
      ? (item.channel_id ? `/channel/${item.channel_id}` : '/feed')
      : `/watch/${item.id}`);
    try {
      if (navigator.share) await navigator.share({ title: item.title, url });
      else { await navigator.clipboard.writeText(url); toast.success('Link copied'); }
    } catch {}
  };

  const cover = resolveFileUrl(item.cover_url) || resolveFileUrl(item.thumb) || '';
  const isVideo = item.kind === 'video';
  const isYouTube = isVideo && item.source?.type === 'youtube' && item.source?.id;

  return (
    <div className="snap-start h-[100svh] w-full relative flex items-center justify-center overflow-hidden" data-testid={`reel-item-${item.id}`}>
      {/* Background — cover art blurred / solid */}
      <div className="absolute inset-0" style={{ background: '#0a0a0c' }} />
      {cover && (
        <img src={cover} alt="" className="absolute inset-0 w-full h-full object-cover opacity-50 blur-2xl scale-110" />
      )}
      <div className="absolute inset-0" style={{ background: 'radial-gradient(ellipse at center, rgba(0,0,0,0.1), rgba(0,0,0,0.75))' }} />

      {/* Foreground media */}
      {item.kind === 'track' && (
        <div className="relative z-10 flex flex-col items-center gap-3 landscape:flex-row landscape:gap-6 landscape:max-[900px]:px-6">
          <div className={`w-[min(62vw,260px)] aspect-square md:w-[340px] rounded-[24px] md:rounded-[28px] overflow-hidden border border-white/10 shadow-2xl landscape:max-[900px]:w-[min(36vh,220px)] ${playing ? 'animate-[pulse_4s_ease-in-out_infinite]' : ''}`}>
            {cover ? (
              <img src={cover} alt={item.title} className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full grid place-items-center" style={{ background: 'linear-gradient(135deg,#a78bfa,#22d3ee)' }}>
                <Music className="w-16 h-16 md:w-20 md:h-20 text-white/70" />
              </div>
            )}
          </div>
          <audio ref={mediaRef} src={resolveFileUrl(item.file_url)} preload="metadata" loop playsInline />
        </div>
      )}

      {isVideo && !isYouTube && (
        <video ref={mediaRef} src={resolveFileUrl(item.source?.url || '')} poster={cover || undefined}
               className="relative z-10 max-h-full max-w-full w-auto h-auto" loop playsInline muted={muted} />
      )}
      {isVideo && isYouTube && (
        <iframe title={item.title} className="relative z-10 w-[min(92vw,420px)] aspect-[9/16] rounded-2xl landscape:max-[900px]:w-auto landscape:max-[900px]:h-[78vh]"
                src={`https://www.youtube-nocookie.com/embed/${item.source.id}?autoplay=${active ? 1 : 0}&mute=1&loop=1&controls=0&playlist=${item.source.id}&playsinline=1`}
                allow="autoplay; encrypted-media" />
      )}

      {/* Overlay — title, artist, actions */}
      <div className="absolute inset-x-0 bottom-0 z-20 p-4 pb-6 md:p-5 md:pb-8 flex items-end gap-2 md:gap-3 bg-gradient-to-t from-black/85 via-black/40 to-transparent">
        <div className="flex-1 min-w-0 pr-2">
          <div className="flex items-center gap-2 mb-1.5 flex-wrap">
            <span className="chip active text-[10px] uppercase">{item.kind === 'track' ? <Music className="w-3 h-3 mr-1 inline" /> : <Film className="w-3 h-3 mr-1 inline" />}{item.kind}</span>
            {item.genre && <span className="chip text-[10px]">{item.genre}</span>}
          </div>
          <h2 className="font-display text-[18px] sm:text-[22px] md:text-[26px] leading-tight line-clamp-2" data-testid="reel-title">{item.title}</h2>
          <Link to={item.channel_id ? `/channel/${item.channel_id}` : '/feed'} className="mt-1.5 inline-flex items-center gap-2 text-white/85 hover:text-white">
            {item.artist_avatar ? (
              <img src={resolveFileUrl(item.artist_avatar)} className="w-6 h-6 md:w-7 md:h-7 rounded-full object-cover" alt="" />
            ) : (
              <div className="w-6 h-6 md:w-7 md:h-7 rounded-full" style={{ background: 'linear-gradient(135deg,#a78bfa,#22d3ee)' }} />
            )}
            <span className="text-[13px] md:text-[14px] font-semibold truncate max-w-[40vw]">{item.artist_name || '—'}</span>
          </Link>
        </div>

        <div className="flex flex-col items-center gap-2 md:gap-3 pb-0.5 shrink-0">
          <button onClick={togglePlay} className="w-10 h-10 md:w-12 md:h-12 rounded-full grid place-items-center bg-white/10 backdrop-blur border border-white/15 hover:bg-white/15" data-testid="reel-play-toggle">
            {playing ? <Pause className="w-4 h-4 md:w-5 md:h-5" /> : <Play className="w-4 h-4 md:w-5 md:h-5" />}
          </button>
          <button onClick={handleLike} className={`w-10 h-10 md:w-12 md:h-12 rounded-full grid place-items-center border backdrop-blur ${liked ? 'bg-[#ff2e63]/80 border-[#ff2e63]' : 'bg-white/10 border-white/15 hover:bg-white/15'}`} data-testid="reel-like">
            <Heart className={`w-4 h-4 md:w-5 md:h-5 ${liked ? 'fill-white' : ''}`} />
          </button>
          <div className="text-[10px] md:text-[11px] text-white/80 -mt-1">{likes}</div>
          <button onClick={handleFollow} className={`w-10 h-10 md:w-12 md:h-12 rounded-full grid place-items-center border backdrop-blur ${following ? 'bg-[#22d3ee]/80 border-[#22d3ee]' : 'bg-white/10 border-white/15 hover:bg-white/15'}`} data-testid="reel-follow">
            {following ? <UserCheck className="w-4 h-4 md:w-5 md:h-5" /> : <UserPlus className="w-4 h-4 md:w-5 md:h-5" />}
          </button>
          <button onClick={share} className="w-10 h-10 md:w-12 md:h-12 rounded-full grid place-items-center bg-white/10 backdrop-blur border border-white/15 hover:bg-white/15" data-testid="reel-share">
            <Share2 className="w-4 h-4 md:w-5 md:h-5" />
          </button>
          {!isYouTube && (
            <button onClick={onToggleMute} className="w-10 h-10 md:w-12 md:h-12 rounded-full grid place-items-center bg-white/10 backdrop-blur border border-white/15 hover:bg-white/15">
              {muted ? <VolumeX className="w-4 h-4 md:w-5 md:h-5" /> : <Volume2 className="w-4 h-4 md:w-5 md:h-5" />}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default function Reel() {
  const { user } = useApp();
  const [items, setItems] = useState([]);
  const [active, setActive] = useState(0);
  const [loading, setLoading] = useState(true);
  const [muted, setMuted] = useState(true);
  const containerRef = useRef(null);

  useEffect(() => {
    (async () => {
      try { setItems(await reelFeed({ limit: 40 })); } catch { toast.error('Could not load feed'); }
      setLoading(false);
    })();
  }, []);

  // Track which item is in viewport
  useEffect(() => {
    const el = containerRef.current; if (!el) return;
    const onScroll = () => {
      const idx = Math.round(el.scrollTop / el.clientHeight);
      setActive(idx);
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => el.removeEventListener('scroll', onScroll);
  }, [items.length]);

  // Keyboard navigation
  useEffect(() => {
    const onKey = (e) => {
      const el = containerRef.current; if (!el) return;
      if (e.key === 'ArrowDown' || e.key === 'j') { e.preventDefault(); el.scrollTo({ top: (active + 1) * el.clientHeight, behavior: 'smooth' }); }
      if (e.key === 'ArrowUp' || e.key === 'k')   { e.preventDefault(); el.scrollTo({ top: Math.max(0, active - 1) * el.clientHeight, behavior: 'smooth' }); }
      if (e.key === 'm') setMuted(m => !m);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [active]);

  const handleLike = async (it) => {
    if (!user) { toast.error('Sign in to like'); return; }
    if (it.kind === 'track') return await toggleTrackLike(it.id);
    return await toggleLike(it.id);
  };
  const handleFollow = async (cid) => {
    if (!user) { toast.error('Sign in to follow'); return; }
    return await toggleSubscribe(cid);
  };

  if (loading) return (
    <div className="h-[100svh] grid place-items-center bg-[#0a0a0c] text-white"><Loader2 className="w-6 h-6 animate-spin" /></div>
  );
  if (!items.length) return (
    <div className="h-[100svh] grid place-items-center bg-[#0a0a0c] text-white p-8 text-center">
      <div>
        <Music className="w-10 h-10 mx-auto text-white/30" />
        <div className="mt-3 font-display text-[22px]">No tracks in the feed yet.</div>
        <div className="text-white/55 text-[13.5px] mt-1">Check back soon — or follow an artist on ReaperCast.</div>
        <Link to="/artists" className="btn-primary mt-5 inline-flex">Browse artists</Link>
      </div>
    </div>
  );

  return (
    <div data-testid="reel-page" className="overflow-x-hidden">
      <div ref={containerRef} className="h-[100svh] overflow-y-auto snap-y snap-mandatory scroll-smooth bg-[#0a0a0c]">
        {items.map((it, i) => (
          <ItemCard key={`${it.kind}-${it.id}`} item={it} active={i === active} muted={muted}
                    onToggleMute={() => setMuted(m => !m)} onLike={handleLike} onFollow={handleFollow} />
        ))}
      </div>
      {/* Side nav controls on desktop */}
      <div className="hidden md:flex fixed right-6 top-1/2 -translate-y-1/2 flex-col gap-2 z-30">
        <button onClick={() => containerRef.current?.scrollTo({ top: Math.max(0, active - 1) * containerRef.current.clientHeight, behavior: 'smooth' })}
                className="w-10 h-10 rounded-full grid place-items-center bg-white/10 backdrop-blur border border-white/15 hover:bg-white/15"><ChevronUp className="w-5 h-5" /></button>
        <button onClick={() => containerRef.current?.scrollTo({ top: (active + 1) * containerRef.current.clientHeight, behavior: 'smooth' })}
                className="w-10 h-10 rounded-full grid place-items-center bg-white/10 backdrop-blur border border-white/15 hover:bg-white/15"><ChevronDown className="w-5 h-5" /></button>
      </div>
    </div>
  );
}
